import { useEffect, useState } from "react";
import { Save, RotateCcw, Info, Bell, MessageSquare } from "lucide-react";
import { adminApi } from "../../api";
import { LoadingText } from "../ui";

interface AppSetting {
  key: string;
  value?: string | null;
  updated_at?: string | null;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function useSetting(key: string) {
  const [value, setValue] = useState("");
  const [original, setOriginal] = useState("");
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  function init(rows: AppSetting[] | null) {
    const row = (rows ?? []).find((s) => s.key === key);
    const v = row?.value ?? "";
    setValue(v);
    setOriginal(v);
    setLastUpdated(row?.updated_at ?? null);
  }

  async function save(trimmedValue: string) {
    setSaving(true);
    setSaved(false);
    try {
      const res = await adminApi.upsertSetting(key, trimmedValue || null);
      setOriginal(trimmedValue || "");
      setLastUpdated(res.data?.updated_at ?? null);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { return; } finally {
      setSaving(false);
    }
  }

  return { value, setValue, original, lastUpdated, saving, saved, init, save };
}

interface SaveRowProps {
  saving: boolean;
  saved: boolean;
  isDirty: boolean;
  onSave: () => void;
  onClear?: () => void;
  showClear: boolean;
}

function SaveRow({ saving, saved, isDirty, onSave, onClear, showClear }: SaveRowProps) {
  return (
    <div className="flex items-center gap-3 pt-1">
      <button
        onClick={onSave}
        disabled={saving || !isDirty}
        className="flex items-center gap-2 rounded-lg bg-[#1D345F] px-5 py-2 text-sm font-medium text-white hover:bg-[#16294d] disabled:opacity-40"
      >
        <Save size={14} />
        {saving ? "Saving…" : saved ? "Saved!" : "Save changes"}
      </button>
      {showClear && (
        <button
          onClick={onClear}
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RotateCcw size={13} />
          Clear
        </button>
      )}
      {isDirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
      {saved && <span className="text-xs text-green-600">Saved — active within 60 seconds</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Agent prompt customisation
// ---------------------------------------------------------------------------

const PROMPT_PLACEHOLDER = `Examples of useful custom instructions:

- Always show monetary values in USD unless the user asks for another currency.
- Our primary sport is football — assume football when the user says "sport" without specifying.
- When users mention "the high street" they mean our retail channel (datasource key: retail).
- Prefer the presentation layer tables (player_sport_daily_summary) over raw transformed tables.
- Our fiscal week starts on Monday.`;

function PromptSection({ rows }: { rows: AppSetting[] | null }) {
  const s = useSetting("prompt.custom_instructions");

  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-init only when rows change
  useEffect(() => { s.init(rows); }, [rows]);

  const isDirty = s.value.trim() !== s.original.trim();

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare size={16} className="text-[#1D345F]" />
        <h3 className="text-sm font-semibold text-gray-900">Agent prompt customisation</h3>
      </div>

      <p className="text-sm text-gray-500">
        Add business-specific instructions appended to the agent's system prompt.
        Changes take effect within 60 seconds — no deployment required.
      </p>

      <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
        <Info size={15} className="mt-0.5 shrink-0 text-blue-500" />
        <p className="text-xs text-blue-700">
          Use this for context the agent can't learn from the schema alone — currency preferences,
          naming conventions, default datasources, or standing instructions for every conversation.
          Core safety rules and SQL restrictions are enforced by code and cannot be overridden here.
        </p>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Custom instructions
          </label>
          {s.lastUpdated && (
            <span className="text-xs text-gray-400">
              Last saved {new Date(s.lastUpdated).toLocaleString()}
            </span>
          )}
        </div>
        <textarea
          value={s.value}
          onChange={(e) => s.setValue(e.target.value)}
          rows={12}
          placeholder={PROMPT_PLACEHOLDER}
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 font-mono text-sm leading-relaxed text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <p className="mt-1 text-right text-xs text-gray-400">{s.value.length} characters</p>
      </div>

      <SaveRow
        saving={s.saving}
        saved={s.saved}
        isDirty={isDirty}
        onSave={() => s.save(s.value.trim())}
        onClear={() => {
          if (!confirm("Clear all custom instructions?")) return;
          s.setValue("");
        }}
        showClear={!!s.value.trim()}
      />

      {s.value.trim() && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Preview — what the agent will see
          </p>
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-gray-700">
            {`Additional instructions from your administrator:\n${s.value.trim()}`}
          </pre>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section: Notifications
// ---------------------------------------------------------------------------

function NotificationsSection({ rows }: { rows: AppSetting[] | null }) {
  const slack = useSetting("notifications.slack_channel_ids");
  const fromName = useSetting("notifications.email_from_name");

  useEffect(() => {
    slack.init(rows);
    fromName.init(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-init only when rows change
  }, [rows]);

  const slackDirty = slack.value.trim() !== slack.original.trim();
  const fromNameDirty = fromName.value.trim() !== fromName.original.trim();
  const isDirty = slackDirty || fromNameDirty;

  async function handleSave() {
    const promises = [];
    if (slackDirty) promises.push(slack.save(slack.value.trim()));
    if (fromNameDirty) promises.push(fromName.save(fromName.value.trim()));
    await Promise.all(promises);
  }

  const saving = slack.saving || fromName.saving;
  const saved = slack.saved || fromName.saved;

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <Bell size={16} className="text-[#1D345F]" />
        <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
      </div>

      <p className="text-sm text-gray-500">
        Configure where alert notifications are sent. These settings override environment variables
        and take effect within 60 seconds.
      </p>

      {/* Slack channel IDs */}
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
          Slack channel IDs
        </label>
        <input
          type="text"
          value={slack.value}
          onChange={(e) => slack.setValue(e.target.value)}
          placeholder="C0123456789, C9876543210"
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">
          Comma-separated Slack channel IDs (e.g. <code className="font-mono">C0123456789</code>).
          Alert rules with "Notify via Slack" enabled will post to these channels.
          Overrides the <code className="font-mono">SLACK_CHANNEL_IDS</code> environment variable.
        </p>
      </div>

      {/* Email from name */}
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
          Email sender name
        </label>
        <input
          type="text"
          value={fromName.value}
          onChange={(e) => fromName.setValue(e.target.value)}
          placeholder="Betable Brian"
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">
          The "From" name shown in alert and report emails. Leave blank to use the default set in
          the <code className="font-mono">EMAIL_FROM_NAME</code> environment variable.
        </p>
      </div>

      <SaveRow
        saving={saving}
        saved={saved}
        isDirty={isDirty}
        onSave={handleSave}
        showClear={false}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AppSettings() {
  const [rows, setRows] = useState<AppSetting[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.getSettings()
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setLoadError("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingText />;
  if (loadError) return <p className="text-sm text-red-600">{loadError}</p>;

  return (
    <div className="max-w-3xl space-y-10">
      <PromptSection rows={rows} />
      <hr className="border-gray-200" />
      <NotificationsSection rows={rows} />
    </div>
  );
}
